<script>
import {
  addWorkShareComment,
  deleteWorkShare,
  getWorkShareDetail,
  updateWorkShareBranchStatus,
} from '@/api/workShare.js'

export default {
  name: 'WorkShareDetailPage',
  data() {
    return {
      loading: false,
      saving: false,
      savingTargetCode: '',
      detail: null,
      statusDrafts: {},
      commentText: '',
      errorMessage: '',
      statusMessage: '',
      postTypes: {
        GENERAL: '일반',
        NOTICE: '공지',
        REQUEST: '요청',
        ISSUE: '이슈',
        SETTLEMENT: '정산',
        DELIVERY: '배송',
        PRODUCT: '상품',
        MEMBER: '회원',
        ETC: '기타',
      },
      priorities: {
        NORMAL: '보통',
        IMPORTANT: '중요',
        URGENT: '긴급',
      },
      branchStatuses: [
        { title: '대기', value: 'OPEN' },
        { title: '확인', value: 'CONFIRMED' },
        { title: '완료', value: 'DONE' },
        { title: '보류', value: 'HOLD' },
      ],
    }
  },
  computed: {
    post() {
      return this.detail?.post || {}
    },
    attachments() {
      return this.detail?.attachments || []
    },
    comments() {
      return this.detail?.comments || []
    },
    branchTargets() {
      return this.detail?.branchTargets || []
    },
    adminTargets() {
      return this.detail?.adminTargets || []
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.errorMessage = ''

      try {
        const res = await getWorkShareDetail(this.$route.params.workSharePostCode)
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '업무공유 상세를 불러오지 못했습니다.'
          return
        }

        this.detail = res.data.data
        this.statusDrafts = Object.fromEntries(this.branchTargets.map(target => [
          target.targetCode,
          {
            workStatus: target.workStatus || 'OPEN',
            statusMemo: target.statusMemo || '',
          },
        ]))
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '업무공유 상세를 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    formatDt(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').substring(0, 16)
    },
    formatSize(size) {
      if (!size) return '-'
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    typeLabel(value) {
      return this.postTypes[value] || value || '-'
    },
    priorityLabel(value) {
      return this.priorities[value] || value || '-'
    },
    statusLabel(value) {
      if (value === 'IN_PROGRESS') return '진행중'
      return this.branchStatuses.find(item => item.value === value)?.title || value || '-'
    },
    statusColor(value) {
      if (value === 'DONE') return 'success'
      if (value === 'CONFIRMED') return 'primary'
      if (value === 'IN_PROGRESS') return 'info'
      if (value === 'HOLD') return 'grey'
      return 'secondary'
    },
    commentWriterLabel(comment) {
      const branchName = comment.writerBranchName || '본사'
      const loginId = comment.writerLoginId || '-'
      const name = comment.writerName || '-'
      return `${branchName} · ${loginId} / ${name}`
    },
    branchStatusDraft(target) {
      if (!this.statusDrafts[target.targetCode]) {
        this.statusDrafts[target.targetCode] = {
          workStatus: target.workStatus || 'OPEN',
          statusMemo: target.statusMemo || '',
        }
      }
      return this.statusDrafts[target.targetCode]
    },
    async saveBranchStatus(target) {
      const draft = this.branchStatusDraft(target)
      this.savingTargetCode = target.targetCode
      this.errorMessage = ''
      this.statusMessage = ''

      try {
        const res = await updateWorkShareBranchStatus(this.post.workSharePostCode, target.targetCode, {
          workStatus: draft.workStatus,
          statusMemo: draft.statusMemo,
        })

        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '상태 변경에 실패했습니다.'
          return
        }

        this.statusMessage = '처리 상태가 저장되었습니다.'
        await this.load()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '상태 변경에 실패했습니다.'
      } finally {
        this.savingTargetCode = ''
      }
    },
    async sendComment() {
      if (!this.commentText.trim()) return
      this.saving = true
      this.errorMessage = ''

      try {
        const res = await addWorkShareComment(this.post.workSharePostCode, this.commentText.trim())
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '댓글 등록에 실패했습니다.'
          return
        }
        this.commentText = ''
        await this.load()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '댓글 등록에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    async remove() {
      if (!window.confirm('게시글을 삭제하시겠습니까?')) return

      try {
        const res = await deleteWorkShare(this.post.workSharePostCode)
        if (res.data?.code === 0) {
          this.$router.push('/work-share')
          return
        }
        this.errorMessage = res.data?.message || '삭제에 실패했습니다.'
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '삭제에 실패했습니다.'
      }
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">업무공유 상세</h1>
        <div class="text-body-2 text-grey">{{ post.writerName || '-' }} · {{ formatDt(post.createdDt) }}</div>
      </div>
      <div class="d-flex ga-2">
        <v-btn variant="text" to="/work-share">목록</v-btn>
        <v-btn variant="tonal" :to="`/work-share/${post.workSharePostCode}/edit`">수정</v-btn>
        <v-btn variant="text" color="error" @click="remove">삭제</v-btn>
      </div>
    </div>

    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
      {{ errorMessage }}
    </v-alert>
    <v-alert v-if="statusMessage" type="success" variant="tonal" class="mb-4">
      {{ statusMessage }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="detail">
      <v-card rounded="lg" elevation="1" class="work-share-card pa-5 mb-4">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
          <div>
            <div class="d-flex ga-2 mb-2">
              <v-chip size="small" color="primary" variant="tonal">{{ typeLabel(post.postType) }}</v-chip>
              <v-chip size="small" color="warning" variant="tonal">{{ priorityLabel(post.priorityType) }}</v-chip>
              <v-chip size="small" :color="statusColor(post.workStatus)" variant="tonal">{{ statusLabel(post.workStatus) }}</v-chip>
            </div>
            <div class="text-h6 font-weight-bold work-share-title">{{ post.boardTitle }}</div>
          </div>
          <div class="text-caption text-grey">종료기한 {{ formatDt(post.dueDt) }}</div>
        </div>

        <div class="work-share-info rounded-lg pa-3 mb-3">
          <div>대상 지점: {{ branchTargets.map(v => v.branchName).join(', ') || '-' }}</div>
          <div>대상 담당자: {{ adminTargets.map(v => v.adminName).join(', ') || '-' }}</div>
          <div v-if="post.referenceUserCode">참조 회원: {{ post.referenceUserName || post.referenceUserEmail || post.referenceUserCode }}</div>
        </div>

        <div v-if="branchTargets.length" class="mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-2">지점별 처리 상태</div>
          <v-table density="compact" class="work-share-read-table">
            <thead>
              <tr>
                <th>지점</th>
                <th>처리상태</th>
                <th>조회여부</th>
                <th>최초 조회</th>
                <th>마지막 조회</th>
                <th>처리정보</th>
                <th style="min-width: 320px">상태 변경</th>
                <th class="text-end">조회수</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="target in branchTargets" :key="target.targetCode">
                <td>{{ target.branchName }}</td>
                <td>
                  <v-chip size="small" :color="statusColor(target.workStatus)" variant="tonal">
                    {{ statusLabel(target.workStatus) }}
                  </v-chip>
                </td>
                <td>
                  <v-chip size="small" :color="target.readYn === 'Y' ? 'success' : 'secondary'" variant="tonal">
                    {{ target.readYn === 'Y' ? '조회' : '미조회' }}
                  </v-chip>
                </td>
                <td class="text-caption">
                  {{ target.firstReadAdminName || '-' }}
                  <span v-if="target.firstReadDt"> · {{ formatDt(target.firstReadDt) }}</span>
                </td>
                <td class="text-caption">
                  {{ target.lastReadAdminName || '-' }}
                  <span v-if="target.lastReadDt"> · {{ formatDt(target.lastReadDt) }}</span>
                </td>
                <td class="text-caption">
                  <div>{{ target.statusUpdatedAdminName || '-' }}</div>
                  <div v-if="target.statusUpdatedDt">{{ formatDt(target.statusUpdatedDt) }}</div>
                  <div v-if="target.statusMemo" class="text-grey">{{ target.statusMemo }}</div>
                </td>
                <td>
                  <div class="branch-status-control">
                    <v-select
                      v-model="branchStatusDraft(target).workStatus"
                      :items="branchStatuses"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-text-field
                      v-model="branchStatusDraft(target).statusMemo"
                      placeholder="처리 메모"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-btn
                      color="primary"
                      variant="tonal"
                      size="small"
                      :loading="savingTargetCode === target.targetCode"
                      @click="saveBranchStatus(target)"
                    >
                      저장
                    </v-btn>
                  </div>
                </td>
                <td class="text-end">{{ target.readCount || 0 }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>

        <div class="work-share-content text-body-2" v-html="post.boardText"></div>
      </v-card>

      <v-card v-if="attachments.length" rounded="lg" elevation="1" class="work-share-card pa-4 mb-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">첨부파일</div>
        <div class="d-flex flex-column ga-2">
          <a
            v-for="file in attachments"
            :key="file.fileCode"
            :href="file.fileUrl"
            target="_blank"
            rel="noopener"
            class="work-share-attachment d-flex align-center justify-space-between rounded border pa-3 text-decoration-none"
          >
            <span class="font-weight-medium">{{ file.originalFileName }}</span>
            <span class="text-caption">{{ formatSize(file.fileSize) }}</span>
          </a>
        </div>
      </v-card>

      <v-card rounded="lg" elevation="1" class="work-share-card pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">댓글</div>
        <div v-for="comment in comments" :key="comment.workShareCommentCode" class="work-share-comment rounded border pa-3 mb-2">
          <div class="d-flex justify-space-between mb-2">
            <span class="font-weight-bold">{{ commentWriterLabel(comment) }}</span>
            <span class="text-caption text-grey">{{ formatDt(comment.createdDt) }}</span>
          </div>
          <div class="text-body-2" style="white-space: pre-wrap">{{ comment.commentText }}</div>
        </div>
        <div v-if="!comments.length" class="text-grey text-body-2 mb-3">등록된 댓글이 없습니다.</div>
        <v-textarea v-model="commentText" label="댓글 입력" variant="outlined" rows="4" auto-grow />
        <div class="d-flex justify-end">
          <v-btn color="primary" :loading="saving" :disabled="!commentText.trim()" @click="sendComment">댓글 등록</v-btn>
        </div>
      </v-card>
    </template>
  </div>
</template>

<style scoped>
.work-share-card {
  color: #e8edf6;
}

.work-share-title {
  color: #f8fafc;
}

.work-share-info {
  background: rgba(148, 163, 184, 0.14);
  color: #dbe5f4;
  line-height: 1.8;
}

.work-share-read-table {
  overflow: hidden;
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  color: #e8edf6;
}

.work-share-read-table :deep(table),
.work-share-read-table :deep(thead),
.work-share-read-table :deep(tbody),
.work-share-read-table :deep(tr),
.work-share-read-table :deep(td),
.work-share-read-table :deep(th) {
  background: transparent !important;
  color: #e8edf6 !important;
}

.branch-status-control {
  display: grid;
  grid-template-columns: 110px minmax(140px, 1fr) 64px;
  gap: 6px;
  align-items: center;
}

.work-share-content {
  color: #eef3fb;
  line-height: 1.8;
}

.work-share-content :deep(*) {
  color: inherit !important;
}

.work-share-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.work-share-content :deep(figure) {
  margin: 16px 0;
}

.work-share-content :deep(table) {
  border-collapse: collapse;
}

.work-share-content :deep(td),
.work-share-content :deep(th) {
  border: 1px solid rgba(148, 163, 184, 0.45);
  padding: 6px 8px;
}

.work-share-attachment,
.work-share-comment {
  border-color: rgba(148, 163, 184, 0.18) !important;
  background: rgba(15, 23, 42, 0.18);
  color: #dbeafe;
}

.work-share-attachment span:last-child {
  color: #bfdbfe;
}
</style>
